import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth } from '../auth/auth-store';
import {
  activeChild,
  NAVIGATION,
  sectionFor,
  sectionIsBuilt,
  type NavSection,
} from './nav/navigation';
import { RAIL_THEMES } from './nav/themes';
import { ChevronIcon, CollapseIcon, PaletteIcon, SearchIcon } from './nav/icons';
import { Notifications } from './Notifications';
import { ConnectionStatus } from './ConnectionStatus';
import { MessagesBell } from './MessagesBell';
import { StatusBell } from './StatusBell';
import { ErrorBoundary } from './ErrorBoundary';
import { Avatar } from '../ui/Avatar';
import { usePreferences } from './preferences-store';
import { useBranding } from './branding';
import { StoredImage } from '../ui/StoredImage';

/**
 * The frame every authenticated screen sits in.
 *
 * A rail down the left because the product has a dozen areas and a horizontal bar runs out of room
 * at about six; a thin top bar for the things that are about the session rather than the area.
 *
 * A group opens *beside* the rail rather than pushing everything below it down. With eleven sections
 * and four items in some of them, expanding in place moves half the menu every time somebody looks
 * at one — and the item they were aiming for is no longer where they last saw it. A panel to the
 * side leaves the rail still.
 *
 * How the rail looks is a preference on the account, not in this browser: see `preferences.tsx`.
 */

export function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  const location = useLocation();
  const { user } = useAuth();
  const { preferences, set } = usePreferences();
  // Paints the document in the tenant's colours, and gives us their logo for the rail.
  const branding = useBranding();

  const [open, setOpen] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const railRef = useRef<HTMLElement>(null);

  const collapsed = preferences.railCollapsed;
  const flyout = preferences.railFlyout;

  // Navigating closes whatever was open: the panel did its job.
  useEffect(() => {
    setOpen(null);
    setDrawer(false);
  }, [location.pathname]);

  // A panel that stays open after a click elsewhere is a panel in the way.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent): void {
      if (!railRef.current?.contains(event.target as Node)) setOpen(null);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(null);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const sections = useMemo(
    () => NAVIGATION.filter((section) => !section.platformOnly || user?.is_platform_admin),
    [user?.is_platform_admin],
  );

  // Which group the current page lives in, so it stays marked even with the panel closed.
  const active = sectionFor(location.pathname)?.key ?? null;

  return (
    <div
      className={`app${collapsed ? ' app--collapsed' : ''}${drawer ? ' app--drawer' : ''}${
        flyout ? ' app--flyout' : ''
      }`}
      data-rail-theme={preferences.railTheme}
    >
      <button
        type="button"
        className="app__scrim"
        aria-label={t('shell.closeMenu')}
        onClick={() => setDrawer(false)}
      />

      <nav className="rail" aria-label={t('shell.mainNav')} ref={railRef}>
        {/*
          * The tenant's logo where this product's mark would be (Phase 118).
          *
          * Replacing the mark rather than sitting beside it: a company that uploaded its logo wants
          * its own product at the top of the screen, and two marks side by side is what a partner
          * page looks like, not what a product does.
          */}
        <div className="rail__brand">
          {branding?.logoFileId ? (
            <StoredImage
              fileId={branding.logoFileId}
              alt={t('app.name')}
              className="rail__logo"
            />
          ) : (
            <>
              <span className="rail__mark" aria-hidden="true">
                SC
              </span>
              <span className="rail__wordmark">{t('app.name')}</span>
            </>
          )}
        </div>

        <ul className="rail__list">
          {sections.map((section) => (
            <RailSection
              key={section.key}
              section={section}
              collapsed={collapsed}
              open={open === section.key}
              inSection={active === section.key}
              onToggle={() => setOpen((current) => (current === section.key ? null : section.key))}
            />
          ))}
        </ul>

        <div className="rail__footer">
          <ThemePicker />

          <button
            type="button"
            className="rail__collapse"
            onClick={() => set('railCollapsed', !collapsed)}
            aria-pressed={collapsed}
          >
            <CollapseIcon />
            <span className="rail__label">{t('shell.collapse')}</span>
          </button>
        </div>
      </nav>

      <div className="app__main">
        <TopBar onOpenMenu={() => setDrawer(true)} />
        <div className="app__content">
          {/* Keyed by route: navigating away from a broken screen clears the error. */}
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

function RailSection({
  section,
  collapsed,
  open,
  inSection,
  onToggle,
}: {
  section: NavSection;
  collapsed: boolean;
  open: boolean;
  inSection: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const Icon = section.icon;
  const { pathname } = useLocation();
  // Exactly one child of a group is the current one; `NavLink`'s prefix matching would light every
  // ancestor as well (see `activeChild`).
  const current = activeChild(section, pathname);
  const trigger = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  /**
   * Where the panel goes.
   *
   * Fixed to the viewport, measured from the button, rather than absolutely positioned inside the
   * rail: the rail scrolls, and a scroll container clips its children in both directions — an
   * absolutely positioned panel simply disappears behind the edge.
   *
   * Measured on open rather than on every render, and nudged up when it would run off the bottom.
   */
  useEffect(() => {
    if (!open || !trigger.current) return;

    function place(): void {
      const button = trigger.current;

      if (!button) return;

      const rect = button.getBoundingClientRect();
      const height = 64 + (section.children?.length ?? 0) * 36;

      setPosition({
        top: Math.min(rect.top - 4, Math.max(8, window.innerHeight - height - 8)),
        left: rect.right + 6,
      });
    }

    place();

    window.addEventListener('resize', place);

    return () => window.removeEventListener('resize', place);
  }, [open, section.children?.length]);

  if (!section.children) {
    return (
      <li>
        <NavLink
          to={section.to!}
          end={section.to === '/'}
          className={({ isActive }) => `rail__item${isActive ? ' rail__item--active' : ''}`}
        >
          <Icon />
          <span className="rail__label">{t(section.labelKey)}</span>
          {!sectionIsBuilt(section.key) && <span className="rail__soon">{t('shell.soon')}</span>}
        </NavLink>
      </li>
    );
  }

  return (
    <li className="rail__group">
      <button
        ref={trigger}
        type="button"
        className={`rail__item rail__item--group${open ? ' rail__item--open' : ''}${
          inSection ? ' rail__item--current' : ''
        }`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <Icon />
        <span className="rail__label">{t(section.labelKey)}</span>
        <ChevronIcon />
      </button>

      {/* Only while open: a panel left in the tree catches pointer events over the content. */}
      {open && (
        <div
          className="rail__panel"
          style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
        >
          <p className="rail__panel-title">{t(section.labelKey)}</p>

          <ul className="rail__sub">
            {section.children.map((child) => (
              <li key={child.key}>
                <NavLink
                  to={child.to}
                  className={`rail__child${child.key === current ? ' rail__child--active' : ''}`}
                >
                  {t(child.labelKey)}
                  {!child.built && <span className="rail__soon">{t('shell.soon')}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Collapsed to icons, the panel is the only way to read the group's name. */}
      {collapsed && !open && <span className="rail__tooltip">{t(section.labelKey)}</span>}
    </li>
  );
}

/** Choosing how the rail looks. Seven palettes, checked against the text they carry. */
function ThemePicker(): React.JSX.Element {
  const { preferences, set, saving } = usePreferences();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ bottom: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    function place(): void {
      const rect = trigger.current?.getBoundingClientRect();

      if (rect) setPosition({ bottom: window.innerHeight - rect.bottom - 4, left: rect.right + 6 });
    }

    place();

    function onPointerDown(event: PointerEvent): void {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', place);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <div className="rail__theme" ref={ref}>
      <button
        ref={trigger}
        type="button"
        className="rail__collapse"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <PaletteIcon />
        <span className="rail__label">{t('shell.appearance')}</span>
      </button>

      {open && (
        <div
          className="rail__panel rail__panel--theme"
          style={
            position
              ? { bottom: position.bottom, left: position.left, top: 'auto' }
              : { visibility: 'hidden' }
          }
        >
          <p className="rail__panel-title">{t('shell.menuColour')}</p>

          <div className="swatches">
            {RAIL_THEMES.map((theme) => (
              <button
                key={theme.key}
                type="button"
                className={`swatch${preferences.railTheme === theme.key ? ' swatch--active' : ''}`}
                style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }}
                title={t(theme.labelKey)}
                aria-label={t(theme.labelKey)}
                aria-pressed={preferences.railTheme === theme.key}
                onClick={() => set('railTheme', theme.key)}
              >
                <span className="swatch__accent" style={{ background: theme.accent }} />
              </button>
            ))}
          </div>

          <label className="rail__toggle">
            <input
              type="checkbox"
              checked={preferences.railFlyout}
              onChange={(event) => set('railFlyout', event.target.checked)}
            />
            {t('shell.flyoutMenus')}
          </label>

          <p className="rail__panel-hint">
            {saving ? t('shell.saving') : t('shell.savedToProfile')}
          </p>
        </div>
      )}
    </div>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }): React.JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Ctrl/Cmd+K focuses search, which is what anybody who uses this kind of product will try.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    }

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar__burger"
        onClick={onOpenMenu}
        aria-label={t('shell.openMenu')}
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>

      <form
        className="topbar__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();

          const query = new FormData(event.currentTarget).get('q');

          if (typeof query === 'string' && query.trim() !== '') {
            navigate(`/busca?q=${encodeURIComponent(query.trim())}`);
          }
        }}
      >
        <SearchIcon />
        <input ref={searchRef} name="q" type="search" placeholder={t('shell.searchPlaceholder')} />
        <kbd aria-hidden="true">Ctrl K</kbd>
      </form>

      <div className="topbar__actions">
        <ConnectionStatus />
        <StatusBell />
        {/* Waiting customers, on every route — the chime used to live inside the inbox screen. */}
        <MessagesBell />
        <Notifications />

        <div className="topbar__user">
          <button
            type="button"
            className="topbar__avatar"
            onClick={() => setMenuOpen((current) => !current)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <Avatar name={user?.name ?? ''} url={user?.avatar_url} size="md" />
            <span className="topbar__name">{user?.name}</span>
          </button>

          {menuOpen && (
            <div className="topbar__menu" role="menu">
              <p className="topbar__menu-head">
                <span className="strong">{user?.name}</span>
                <span className="muted">{user?.email}</span>
              </p>

              <NavLink role="menuitem" to="/configuracoes/conta" onClick={() => setMenuOpen(false)}>
                {t('nav.account')}
              </NavLink>

              <button type="button" role="menuitem" onClick={() => void logout()}>
                {t('home.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
